import { inject } from '@angular/core';
import { firstValueFrom, forkJoin, Observable } from 'rxjs';

import {
  BudgetType,
  CreateBudgetAttributeInput,
  EntitySettingsInput,
  EntitySettingTypes,
  EntityType,
  GqlService,
  RemoveBudgetAttributeInput,
  RenameBudgetAttributeInput,
} from '@shared/services/gql.service';
import { customInjectMutation } from '@shared/utils/custom-mutation.utils';
import { gqlToPromise } from '@shared/utils/gql-to-promise';

import {
  BalanceSheetEntitySettingsPayload,
  NewBudgetAttribute,
  PendingGridChanges,
  RemovedBudgetAttribute,
  RenamedBudgetAttribute,
} from '../new-manage-balance-sheets-accounts-component-modal.component';
import { mergePendingChangesIntoVendorSettings } from '../services/balance-sheet-save.utils';

type SaveBalanceSheetUserInput = {
  entitySettings: BalanceSheetEntitySettingsPayload;
  vendorIds: string[];
};

export const injectSaveBalanceSheetEntitySettings = () => {
  const gqlService = inject(GqlService);

  return customInjectMutation({
    coreMutationFn: (gqlApiParams) => {
      return gqlToPromise(() => gqlService.updateEntitySettings$(gqlApiParams));
    },
    mutationKey: ['save-balance-sheet-entity-settings'],
    transformer: (userInput: SaveBalanceSheetUserInput): EntitySettingsInput => ({
      entity_ids: userInput.vendorIds,
      entity_type: EntityType.ORGANIZATION,
      setting_type: EntitySettingTypes.VENDOR_ACCOUNTS,
      settings: JSON.stringify(userInput.entitySettings),
    }),
  });
};

const populateBudgetAttributePromisesHelper = (params: {
  notes: string;
  pendingChanges: PendingGridChanges;
  supporting_document_s3_bucket_keys: string[];
  transaction_id: string;
  vendor_id: string;
}) => {
  const { notes: note, transaction_id, vendor_id } = params;

  const allPromises = {
    newBudgetAttributes: [] as CreateBudgetAttributeInput[],
    removedBudgetAttributes: [] as RemoveBudgetAttributeInput[],
    renamedBudgetAttributes: [] as RenameBudgetAttributeInput[],
  };

  const baseParams = {
    budget_type: BudgetType.BUDGET_PRIMARY,
    note,
    transaction_id,
    vendor_id,
  };

  allPromises.newBudgetAttributes.push(
    ...params.pendingChanges.newBudgetAttributes.map((attr: NewBudgetAttribute) => ({
      name: attr.name,
      ...baseParams,
    }))
  );

  allPromises.removedBudgetAttributes.push(
    ...params.pendingChanges.removedBudgetAttributes.map((attr: RemovedBudgetAttribute) => ({
      name: attr.name,
      ...baseParams,
    }))
  );

  allPromises.renamedBudgetAttributes.push(
    ...params.pendingChanges.renamedBudgetAttributes.map((attr: RenamedBudgetAttribute) => ({
      name: attr.name,
      new_name: attr.new_name,
      ...baseParams,
    }))
  );

  return allPromises;
};

type ProcessBudgetAttributeChangesParams = {
  notes: string;
  pendingChanges: PendingGridChanges;
  supporting_document_s3_bucket_keys: string[];
  transaction_id: string;
  vendor_id: string;
};

export const injectProcessBudgetAttributeChanges = () => {
  const gqlService = inject(GqlService);

  return customInjectMutation({
    coreMutationFn: (params) => {
      const promises: Observable<unknown>[] = [];

      params.newBudgetAttributes.forEach((attr) => {
        promises.push(gqlService.createBudgetAttribute$(attr));
      });
      params.removedBudgetAttributes.forEach((attr) => {
        promises.push(gqlService.removeBudgetAttribute$(attr));
      });
      params.renamedBudgetAttributes.forEach((attr) => {
        promises.push(gqlService.renameBudgetAttribute$(attr));
      });

      return firstValueFrom(forkJoin(promises));
    },
    mutationKey: ['process-budget-attribute-changes'],
    transformer: (userInput: ProcessBudgetAttributeChangesParams) => {
      return populateBudgetAttributePromisesHelper(userInput);
    },
  });
};

type BatchUpdateEntitySettingsParams = {
  allVendorIds: string[];
  allVendorSettings: Record<string, BalanceSheetEntitySettingsPayload>;
  applyToAll: boolean;
  notes: string;
  pendingChanges: PendingGridChanges;
  selectedVendorId: string;
  supportingDocumentS3BucketKeys: string[];
};

export const injectBatchUpdateEntitySettingsMutation = () => {
  const gqlService = inject(GqlService);

  return customInjectMutation({
    coreMutationFn: (input: EntitySettingsInput[]) => {
      return gqlToPromise(() => gqlService.batchUpdateEntitySettings$(input));
    },
    mutationKey: ['batch-update-entity-settings'],
    transformer: (params: BatchUpdateEntitySettingsParams): EntitySettingsInput[] => {
      const { meta_data, payload: merged } = mergePendingChangesIntoVendorSettings(params);

      return params.allVendorIds.map((vendorId) => ({
        entity_ids: [vendorId],
        entity_type: EntityType.ORGANIZATION,
        meta_data,
        setting_type: EntitySettingTypes.VENDOR_ACCOUNTS,
        settings: JSON.stringify({
          ...merged[vendorId],
          APPLY_TO_ALL: params.applyToAll,
          SELECTED_VENDOR_ID: vendorId === params.selectedVendorId ? params.selectedVendorId : null,
        }),
      }));
    },
  });
};
